import { matchesPath } from "./navigation";

// Applications = the business domains of the V2 menu (CDC § 14.1), each with its own short menu. Pure rules, unit-tested in
// apps.test.js: Layout.jsx decides what each person may see (a section's `items[].visible`), this file only arranges it.

export const RECENTS_KEY = "jamora.nav.recents";
export const FAVORITES_KEY = "jamora.nav.favorites";
export const MAX_RECENTS = 8;

// One accent colour per application, so Support or Finances is recognised without reading: full class names (Tailwind needs them
// literally), kept subtle — a tinted icon tile and a hover border.
export const ACCENTS = {
  blue: { tile: "bg-blue-50 text-blue-600", hover: "hover:border-blue-300" },
  teal: { tile: "bg-teal-50 text-teal-600", hover: "hover:border-teal-300" },
  emerald: { tile: "bg-emerald-50 text-emerald-600", hover: "hover:border-emerald-300" },
  orange: { tile: "bg-orange-50 text-orange-600", hover: "hover:border-orange-300" },
  violet: { tile: "bg-violet-50 text-violet-600", hover: "hover:border-violet-300" },
  indigo: { tile: "bg-indigo-50 text-indigo-600", hover: "hover:border-indigo-300" },
  sky: { tile: "bg-sky-50 text-sky-600", hover: "hover:border-sky-300" },
  rose: { tile: "bg-rose-50 text-rose-600", hover: "hover:border-rose-300" },
  lime: { tile: "bg-lime-50 text-lime-700", hover: "hover:border-lime-300" },
  slate: { tile: "bg-slate-100 text-slate-600", hover: "hover:border-slate-300" },
  pink: { tile: "bg-pink-50 text-pink-600", hover: "hover:border-pink-300" },
  amber: { tile: "bg-amber-50 text-amber-600", hover: "hover:border-amber-300" },
  fuchsia: { tile: "bg-fuchsia-50 text-fuchsia-600", hover: "hover:border-fuchsia-300" },
  cyan: { tile: "bg-cyan-50 text-cyan-600", hover: "hover:border-cyan-300" },
  stone: { tile: "bg-stone-100 text-stone-700", hover: "hover:border-stone-300" },
  brand: { tile: "bg-white", hover: "hover:border-teal-300" }, // Jamora App: the logo brings its own colours
};
export const accentOf = (name) => ACCENTS[name] || ACCENTS.slate;

// The applications centre groups the tiles by category (user's decision 2026-10-24). One map keeps the grouping in a single place: an
// application not listed here (a future one) lands in « Autres » until it is placed. The order of CATEGORIES is the order on screen.
export const CATEGORIES = [
  { id: "tools", label: "Outils" },
  { id: "sales", label: "Vente et clients" },
  { id: "support", label: "Support et produit" },
  { id: "operations", label: "Opérations" },
  { id: "finance", label: "Finance et administratif" },
  { id: "platform", label: "Plateforme" },
];
export const OTHER_CATEGORY = { id: "other", label: "Autres" };
export const APP_CATEGORY = {
  copilot: "tools", cockpit: "tools",
  commercial: "sales", customers: "sales", revenue: "sales", marketing: "sales",
  support: "support", product: "support",
  field: "operations", team: "operations", purchases: "operations",
  finance: "finance", documents: "finance", governance: "finance",
  "jamora-admin": "platform", admin: "platform",
};

// Applications arranged by category, in the fixed order; a category with nothing to show is left out.
export function groupByCategory(apps) {
  const known = new Set(CATEGORIES.map((category) => category.id));
  const categoryOf = (app) => (known.has(APP_CATEGORY[app.id]) ? APP_CATEGORY[app.id] : OTHER_CATEGORY.id);
  return [...CATEGORIES, OTHER_CATEGORY]
    .map((category) => ({ ...category, apps: apps.filter((app) => categoryOf(app) === category.id) }))
    .filter((group) => group.apps.length > 0);
}

// One application per section that has at least one visible entry. `to` = where its tile and its sidebar link lead (the first
// entry that can really be opened); an application made only of planned modules is `upcoming` (greyed, never a link).
export function buildApps(sections) {
  const apps = [];
  for (const section of sections) {
    if (section.flat) continue;
    const items = section.items.filter((item) => item.visible);
    if (!items.length) continue;
    const real = items.filter((item) => !item.upcoming && item.to);
    apps.push({
      id: section.id,
      label: section.label,
      description: section.description || "",
      icon: section.icon,
      accent: section.accent,
      items,
      to: real[0]?.to || null,
      upcoming: real.length === 0,
      badge: items.reduce((sum, item) => sum + (item.badge > 0 ? item.badge : 0), 0) || null,
    });
  }
  return apps;
}

// The application whose menu holds the current page; null for the pages outside any application (dashboard, tasks, copilot…).
export function appIdOfPath(apps, pathname) {
  return apps.find((app) => app.items.some((item) => !item.upcoming && matchesPath(item, pathname)))?.id || null;
}

// Pages that belong to no menu entry but still deserve a trail.
const EXTRA_PAGES = [{ to: "/profile", label: "Mon profil" }];
const underPath = (to, pathname) => pathname === to || pathname.startsWith(`${to}/`);

// The trail shown in the header on a computer. Inside an application: « Centre d'applications › Application › Page » (the page is left out
// when it bears the application's own name). Outside any application: « Tableau de bord » alone on the home page, « Tableau de bord › Page »
// for the other global pages (tasks, Copilote…) and the few pages with no menu entry (profile). Nothing for an unknown page.
// `kind` lets the header give each crumb its own test id; the last crumb is the page being read.
export function breadcrumbOf(apps, globalItems, pathname, appsPage = "/apps") {
  const root = { kind: "apps", label: "Centre d'applications", to: appsPage };
  if (pathname === appsPage) return [root];
  for (const app of apps) {
    const entry = app.items.find((candidate) => !candidate.upcoming && matchesPath(candidate, pathname));
    if (!entry) continue;
    const trail = [root, { kind: "app", label: app.label, to: app.to || appsPage }];
    return entry.label === app.label ? trail : [...trail, { kind: "page", label: entry.label, to: entry.to }];
  }
  const home = globalItems.find((candidate) => candidate.id === "dashboard" && candidate.to);
  const global = globalItems.find((candidate) => candidate.to && !candidate.upcoming && matchesPath(candidate, pathname));
  if (global) return global === home ? [{ kind: "page", label: global.label, to: global.to }] : [...(home ? [{ kind: "home", label: home.label, to: home.to }] : []), { kind: "page", label: global.label, to: global.to }];
  const extra = EXTRA_PAGES.find((page) => underPath(page.to, pathname));
  return extra ? [...(home ? [{ kind: "home", label: home.label, to: home.to }] : []), { kind: "page", label: extra.label, to: extra.to }] : [];
}

// Every entry a person can open, by id, with the application it belongs to (null for the global ones).
export function entryIndex(apps, globalItems) {
  const index = new Map();
  globalItems.filter((item) => item.to).forEach((item) => index.set(item.id, { item, appId: null }));
  apps.forEach((app) => app.items.filter((item) => !item.upcoming && item.to).forEach((item) => index.set(item.id, { item, appId: app.id })));
  return index;
}

const fold = (text) => String(text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// The applications centre's search and « Mes favoris » filter; accents and case ignored, name, description and entry names searched.
export function filterApps(apps, { query = "", onlyFavorites = false, favorites = [] } = {}) {
  const wanted = fold(query).trim();
  return apps.filter((app) => {
    if (onlyFavorites && !favorites.includes(app.id)) return false;
    if (!wanted) return true;
    return [app.label, app.description, ...app.items.map((item) => item.label)].some((text) => fold(text).includes(wanted));
  });
}

export function toggleFavorite(favorites, id) {
  return favorites.includes(id) ? favorites.filter((fav) => fav !== id) : [...favorites, id];
}

// Most recent first, one line per entry, capped.
export function pushRecent(recents, id, at) {
  return [{ id, at }, ...recents.filter((recent) => recent.id !== id)].slice(0, MAX_RECENTS);
}

// Recents still reachable by this person (a right removed, a module switched off: the line just disappears).
export function resolveRecents(recents, index) {
  return recents.filter((recent) => index.has(recent.id)).map((recent) => ({ ...recent, ...index.get(recent.id) }));
}

export function timeAgo(at, now = Date.now()) {
  const minutes = Math.floor((now - at) / 60000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "Hier" : `Il y a ${days} j`;
}

// « À traiter » per application, from the dashboard's todo lines (each carries the page it opens): a line counts for the
// application whose menu holds that page; lines opening a page outside any application (tasks) count for none.
// Result: { appId: { total, texts: ["1 demande d'assistance en attente", …] } } — only applications with something waiting.
export function appCounts(apps, lines) {
  const counts = {};
  for (const line of lines) {
    const appId = appIdOfPath(apps, String(line.to || "").split("?")[0]);
    if (!appId || !(line.count > 0)) continue;
    const entry = counts[appId] || { total: 0, texts: [] };
    entry.total += line.count;
    entry.texts.push(line.text);
    counts[appId] = entry;
  }
  return counts;
}

export function readStored(key, fallback) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return Array.isArray(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

export function writeStored(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not kept in a private window: it still works for this visit.
  }
}
